import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Reveal } from "@/components/landing/Reveal";
import { scrollToId } from "@/lib/site";

const CountUp = ({ to, decimals = 0, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: "easeOut",
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, to]);

  return (
    <span ref={ref} className="font-display text-5xl font-extrabold tracking-tight text-brand-blue sm:text-6xl">
      {val.toFixed(decimals)}
      <span className="text-fall-orange">{suffix}</span>
    </span>
  );
};

const STATS = [
  { to: 20, suffix: "M+", text: "Americans are allergic to dust mites — the #1 thing hiding in your bedding" },
  { to: 1.5, decimals: 1, suffix: "M", text: "dust mites can live in ONE unwashed comforter, feeding on dead skin" },
  { to: 80, suffix: "%", text: "of asthma attacks in allergy sufferers are triggered by dirty bedding" },
];

export const Stats = () => (
  <section data-testid="dust-mite-stats" className="bg-white py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <Reveal className="mx-auto max-w-3xl text-center">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-blue sm:text-sm">
          When did you last wash your comforter?
        </p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl lg:text-4xl">
          What&apos;s living in your bedding?
        </h2>
      </Reveal>

      <Reveal delay={0.1} className="mt-10">
        <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-sm">
          <img
            data-testid="stats-comforter-image"
            src="/images/blanket_clean.webp"
            onError={(e) => (e.currentTarget.src = "/images/blanket_clean.png")}
            alt="Freshly washed fluffy white comforters stacked and clean"
            className="h-64 w-full object-cover sm:h-80"
          />
        </div>
      </Reveal>

      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {STATS.map((s, i) => (
          <Reveal key={i} delay={i * 0.12}>
            <div className="h-full rounded-3xl border border-slate-200 bg-slate-50 p-8 transition-shadow hover:shadow-xl hover:shadow-brand-blue/10">
              <CountUp to={s.to} decimals={s.decimals || 0} suffix={s.suffix} />
              <p className="mt-4 text-base leading-relaxed text-slate-600">{s.text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2} className="mt-10">
        <div className="flex flex-col items-center justify-between gap-5 rounded-3xl bg-gradient-to-r from-brand-navy via-brand-navy-light to-brand-navy p-8 text-center sm:flex-row sm:text-left">
          <p className="text-base font-semibold text-white">
            Experts recommend a professional deep clean every 2–3 months.{" "}
            <span className="text-amber-300">How long has it been?</span>
          </p>
          <button
            data-testid="stats-fall-special-cta"
            onClick={() => scrollToId("fall-special")}
            className="shrink-0 rounded-full bg-fall-amber px-6 py-3 font-display text-sm font-extrabold uppercase tracking-widest text-brand-navy transition-transform hover:scale-105"
          >
            Clean mine for $19.99
          </button>
        </div>
      </Reveal>
    </div>
  </section>
);
